import { i18n } from '@/common/locale';
import {
  PaginationOption,
  TokenFilterOption,
  TextFilterOption,
  GridSortingOption,
  GridTreeViewOption,
  GridRowSelectionOption,
  GridStyleOption,
  GridDetailRowOption,
  GridExpandingOption,
  GridColumnResizingOption,
  GridColumnOrderingOption,
  GridColumnPinningOption,
  GridColumnVisibilityOption,
  GridSettingOption,
  GridRowMenuOption,
  GridColumnAutoFitOption,
  GridGroupingOption,
  GridSummaryOption,
  GridOptionV2,
} from './modernGrid.types';

export const SCROLL_BAR_WIDTH = 10;
export const INITIAL_PAGE_INDEX = 0;
export const INITIAL_PAGE_SIZE = 20;
export const DEFAULT_EXTRA_COLUMN_SIZE = 40;
export const DEFAULT_COLUMN_SIZE = 120;
export const DEFAULT_COLUMN_MIN_SIZE = 100;
export const TREE_INDENT_SIZE = 13;
export const TREE_EXPAND_AREA_SIZE = 20;
export const COLUMN_RESIZING_HANDLE_BAR_SIZE = 4;
export const DEFAULT_HEADER_HEIGHT = 36;
export const DEFAULT_ROW_HEIGHT = 28;
export const CHECKBOX_COLUMN_ID = 'checkbox';
export const EXPAND_BTN_COLUMN_ID = 'expandBtn';
export const NUMBER_COLUMN_ID = 'number';

export const defaultPagination: PaginationOption = {
  use: false,
  useClient: false,
  align: 'right',
  showSetting: false,
  showPageInfo: false,
  perData: 20,
  items: [20, 30, 40, 50],
  total: 0,
};

export const defaultTokenFilter: TokenFilterOption = {
  use: false,
  useClient: true,
  mode: 'full',
  storageKey: '',
  filterItems: [],
};

export const defaultTextFilter: TextFilterOption = {
  use: false,
  useClient: false,
  mode: 'full',
  placeholder: '',
  maxLength: 50,
};

export const getDefaultSortingOption = (): GridSortingOption => ({
  enable: true,
  multi: true,
  columnMenu: {
    ascending: {
      use: true,
      title: i18n.global.t('WORD.ASCENDING'),
      icon: 'ev-icon-allow2-up',
    },
    descending: {
      use: true,
      title: i18n.global.t('WORD.DESCENDING'),
      icon: 'ev-icon-allow2-down',
    },
  },
});

export const defaultSortingOption = getDefaultSortingOption();

export const defaultGroupingOption: GridGroupingOption = {
  use: false,
  groupByColumns: [],
  groupedColumnMode: false,
};

export const defaultSummaryOption: GridSummaryOption = {
  use: false,
};

export const defaultExpandingOption: GridExpandingOption = {
  useHeaderExpanding: false,
  expandAllOnLoad: true,
  expandedIcon: 'icon-fill-arrow-down',
  collapsedIcon: 'icon-fill-arrow-right',
  filterFromLeafRows: true,
  paginateExpandedRows: false,
};

export const defaultTreeViewOption: GridTreeViewOption = {
  use: false,
  subRowKey: 'children',
};

export const defaultDetailRowOption: GridDetailRowOption = {
  use: false,
  detailRowKey: 'detail',
};

export const defaultRowSelectionOption: GridRowSelectionOption = {
  enableSelect: true,
  useCheckbox: false,
  useCheckAll: true,
  useMultiCheck: true,
  enableSubRowCheck: true,
  multiple: false,
  limitCount: 0,
  disableDeselect: false,
  disableCellClickSelect: false,
  mode: 'multi',
};

export const defaultColumnResizingOption: GridColumnResizingOption = {
  use: true,
  columnResizeMode: 'onEnd',
  columnMinSize: DEFAULT_COLUMN_MIN_SIZE,
  columnMaxSize: undefined,
  enableReset: true,
};

export const defaultColumnAutoFitOption: GridColumnAutoFitOption = {
  use: true,
  columnMinSize: DEFAULT_COLUMN_MIN_SIZE,
};

export const defaultColumnOrderingOption: GridColumnOrderingOption = {
  use: true,
  useHandleBtn: false,
  handleIcon: 'ev-icon-hamburger2',
};

export const defaultColumnPinningOption: GridColumnPinningOption = {
  leftPinningKeys: [],
  rightPinningKeys: [],
  useLeftSticky: false,
  useRightSticky: false,
};

export const getDefaultColumnVisibilityOption = (): GridColumnVisibilityOption => ({
  columnMenu: {
    hidden: {
      use: true,
      title: i18n.global.t('WORD.HIDE'),
      icon: 'icon-visibility-off',
    },
  },
});

export const defaultColumnVisibilityOption = getDefaultColumnVisibilityOption();

export const getDefaultGridSettingOption = (): GridSettingOption => ({
  use: true,
  useResetUserEnv: {
    use: true,
    title: i18n.global.t('WORD.RESET_USER_ENV'),
    icon: '',
  },
  useExportExcel: {
    use: true,
    title: i18n.global.t('WORD.EXPORT_EXCEL'),
    icon: '',
  },
  useColumnVisibility: {
    use: true,
    title: i18n.global.t('WORD.COLUMN_LIST'),
    icon: '',
  },
});

export const defaultGridSettingOption = getDefaultGridSettingOption();

export const defaultRowMenuOption: GridRowMenuOption = {
  use: false,
  menuItems: [],
};

export const defaultStyleOption: GridStyleOption = {
  width: '100%',
  height: '100%',
  showBorder: true,
  header: {
    show: true,
    useSticky: true,
    height: DEFAULT_HEADER_HEIGHT,
    showColumnLine: true,
    showRowLine: true,
    textSize: '12px',
    textWeight: '700',
  },
  body: {
    rowHeight: DEFAULT_ROW_HEIGHT,
    useStripe: false,
    showColumnLine: true,
    showRowLine: true,
    textSize: '12px',
    textWeight: '400',
    highlightedColor: 'var(--color-blue-09-01)',
  },
};

export const defaultUseNumberColumn = false;

/**
 * 컬럼 이름 기반 사이즈 매칭 규칙
 * 우선순위: 복합/단일 매칭 > 포함 매칭
 */
export interface ColumnNameSizeRule {
  keywords: string[];
  size: number;
}

export const COLUMN_NAME_SIZE_EXACT_RULES: ColumnNameSizeRule[] = [
  { keywords: ['tid'], size: 180 },
  { keywords: ['namespace'], size: 180 },
];

export const COLUMN_NAME_SIZE_CONTAINS_RULES: ColumnNameSizeRule[] = [
  { keywords: ['txn', 'name'], size: 240 },
  { keywords: ['sql', 'text'], size: 350 },
  { keywords: ['plan', 'text'], size: 300 },
  { keywords: ['class', 'name'], size: 200 },
  { keywords: ['cluster', 'name'], size: 180 },
  { keywords: ['pod', 'name'], size: 240 },
  { keywords: ['query'], size: 350 },
  { keywords: ['desc'], size: 270 },
  { keywords: ['message'], size: 200 },
  { keywords: ['name'], size: 180 },
  { keywords: ['alias'], size: 180 },
  { keywords: ['instance'], size: 180 },
  { keywords: ['address'], size: 140 },
  { keywords: ['schema'], size: 120 },
  { keywords: ['type'], size: 110 },
  { keywords: ['kind'], size: 110 },
  { keywords: ['ip'], size: 110 },
  { keywords: ['time'], size: 150 },
];

export const defaultTooltipOption = 'list' as const;

/**
 * 트리뷰용 기본 그리드 옵션
 * listViewTreeGridOption과 유사한 설정을 modernGrid 형식으로 제공
 */
export const listViewTreeGridOptionV2: GridOptionV2 = {
  treeView: {
    use: true,
    subRowKey: 'children',
  },
  expanding: defaultExpandingOption,
  columnResizing: defaultColumnResizingOption,
  gridSetting: defaultGridSettingOption,
  rowSelection: {
    ...defaultRowSelectionOption,
    enableSelect: true,
  },
  style: {
    ...defaultStyleOption,
    body: {
      ...defaultStyleOption.body,
      rowHeight: DEFAULT_ROW_HEIGHT,
    },
  },
};
